import { Box, Divider, Icon } from '@mui/material';
import { FC, PropsWithChildren, useCallback } from 'react';
import { createPortal } from 'react-dom';

import CertifyProgressBar from '../../components/domain/CertifyProgressBar';
import { Row, Section, SectionRow } from '../../components/shared/Boxes';
import { Title } from '../../components/shared/Typographies';
import { useCertifyCtx } from '../../contexts/CertifyCtx';

type Props = PropsWithChildren & {
  title?: string;
  desc?: string;
};

const TITLE_ID = 'CertifyLayout__title';
const DESCRIPTION_ID = 'CertifyLayout__description';

const CertifyLayout: FC<Props> = ({ title, desc, children }) => {
  const {
    values: { pet, info },
  } = useCertifyCtx();

  return (
    <Box sx={{ pb: 2 }}>
      {pet && (
        <SectionRow
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            flexDirection: 'row-reverse',
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{ overflow: 'scroll' }}>
            <ResetButton>
              <Icon fontSize="small">close</Icon>
            </ResetButton>
          </Box>
          <Box>
            <CertifyProgressBar {...{ pet, info }} />
          </Box>
        </SectionRow>
      )}
      <Section>
        <div id={TITLE_ID} />
        <div id={DESCRIPTION_ID} />
      </Section>
      {children}
      <br />

      <Divider />
      <Section sx={{ my: 2, mb: 1 }}>
        <ResetButton>認定を中断して最初からやり直す場合はこちら</ResetButton>
      </Section>
    </Box>
  );
};

const ResetButton = ({ children }: PropsWithChildren) => {
  const { step, reset } = useCertifyCtx();
  const handleClose = useCallback(() => {
    if (window.confirm('認定を中断して最初からやり直しますか？')) {
      reset();
    }
  }, [reset]);

  if (step.type === 'pet' || step.type === 'complete') return null;

  return (
    <Box
      sx={{
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        lineHeight: 1,
        fontSize: 12,
        color: 'text.secondary',
      }}
      onClick={handleClose}
    >
      {children}
    </Box>
  );
};

export default CertifyLayout;

export const CertifyTitle: FC<PropsWithChildren> = ({ children }) => {
  return createPortal(
    <Title>{children}</Title>,
    document.getElementById(TITLE_ID) ?? document.createElement('div')
  );
};

export const CertifyDescription: FC<PropsWithChildren> = ({ children }) => {
  return createPortal(
    <Row>{children}</Row>,
    document.getElementById(DESCRIPTION_ID) ?? document.createElement('div')
  );
};
